import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";

export interface CoverageFeeProductInput {
  vendor?: string | null;
  sku?: string | null;
  title?: string | null;
  product_id?: string | number | null;
  productId?: string | number | null;
  name?: string | null;
}

/**
 * For call sites with no access to the team's coverage product ids (e.g.
 * browser code). Matching falls back to vendor/sku/title heuristics only.
 */
export const NO_COVERAGE_PRODUCT_IDS: ReadonlySet<string> = new Set();

export enum CoverageFeeProductMatchType {
  REDO_ONLY = "redo_only",
  REDO_AND_COMPETITORS = "redo_and_competitors",
  STRICT_PRODUCT_ID_CHECK = "strict_product_id_check",
}

function coverageIdsInclude(
  redoCoverageProductIds: ReadonlySet<string>,
  productId: string,
): boolean {
  if (!productId) {
    return false;
  }
  if (redoCoverageProductIds.has(productId)) {
    return true;
  }
  for (const id of redoCoverageProductIds) {
    if (parseNumericIdFromShopifyId(id) === productId) {
      return true;
    }
  }
  return false;
}

export function isCoverageFeeProduct(args: {
  product: CoverageFeeProductInput;
  redoCoverageProductIds: ReadonlySet<string>;
  matchType: CoverageFeeProductMatchType;
}): boolean {
  const { product, redoCoverageProductIds, matchType } = args;
  const vendor = product.vendor?.toLowerCase().trim() ?? "";
  const sku = product.sku?.toLowerCase() ?? "";
  const title =
    product.title?.toLowerCase() ?? product.name?.toLowerCase() ?? "";
  /** Shopify payloads pass numeric product ids at runtime despite the type. */
  const productId = parseNumericIdFromShopifyId(
    String(product.product_id ?? product.productId ?? ""),
  );

  if (matchType === CoverageFeeProductMatchType.STRICT_PRODUCT_ID_CHECK) {
    return coverageIdsInclude(redoCoverageProductIds, productId);
  }

  if (
    /^re:?do$/.test(vendor) ||
    sku.startsWith("x-redo") ||
    title.includes("re:do") ||
    title.includes("redo fee") ||
    productId.startsWith("redo-shipping-protection-") ||
    coverageIdsInclude(redoCoverageProductIds, productId)
  ) {
    return true;
  }

  if (
    matchType === CoverageFeeProductMatchType.REDO_AND_COMPETITORS &&
    (/route-/i.test(sku) ||
      /routeins/i.test(sku) ||
      vendor === "order protection" ||
      /insurance|package\s+protection|order\s+protection/i.test(title))
  ) {
    return true;
  }

  return false;
}
